import React, { useContext, useState } from 'react';
import styled from 'astroturf/react';
import { CiterightContext } from './CiterightContextProvider';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Banner } from 'js/design_system/Banner';
import { Icon } from 'js/design_system/Icon';
import { useCiterightCitations } from './hooks/useCiterightCitations';
import WorkCard from './components/WorkCard';

interface PreprintExportStepProps {
  onGoToStep3?: () => void;
}

export const PreprintExportStep: React.FC<PreprintExportStepProps> = ({
  onGoToStep3,
}) => {
  const context = useContext(CiterightContext);
  const {
    generateCitationsForSavedReferences,
    generateBibtexForSavedReferences,
    generateRisForSavedReferences,
    error,
    clearError,
  } = useCiterightCitations();
  const [loadingButton, setLoadingButton] = useState<
    'ris' | 'bibtex' | 'copy' | null
  >(null);

  if (!context) {
    return null;
  }

  const { savedReferences, reportId } = context;
  const savedCount = savedReferences.length;

  const handleDownloadRis = async () => {
    if (!savedReferences.length || loadingButton) return;

    setLoadingButton('ris');
    await generateRisForSavedReferences(savedReferences);
    setLoadingButton(null);
  };

  const handleDownloadBibtex = async () => {
    if (!savedReferences.length || loadingButton) return;

    setLoadingButton('bibtex');
    await generateBibtexForSavedReferences(savedReferences);
    setLoadingButton(null);
  };

  const handleCopyCitations = async () => {
    if (!savedReferences.length || loadingButton) return;

    setLoadingButton('copy');
    try {
      const citations =
        await generateCitationsForSavedReferences(savedReferences);
      const text = citations
        .filter((c) => c.name === 'APA')
        .map((c, i) => `${i + 1}. ${c.citation}`)
        .join('\n\n');
      await navigator.clipboard.writeText(text);
    } finally {
      setLoadingButton(null);
    }
  };

  return (
    <Container>
      <SectionHeader>
        <Body size="sm" bold>
          Your saved references ({savedCount})
        </Body>
        {savedCount > 0 && (
          <Button
            size="sm"
            icon="file_download"
            disabled={loadingButton === 'ris'}
            trackClick={{
              source: 'citeright.references_export',
              entityId: reportId || undefined,
              extra: {
                format: 'ris',
                reference_count: savedReferences.length,
              },
            }}
            onClick={handleDownloadRis}
          >
            Download saved references for EndNote, Mendeley, Zotero
          </Button>
        )}
      </SectionHeader>

      {error && (
        <Banner
          type="danger"
          icon={<Icon name="error_outline" size={16} />}
          actions={[
            {
              content: 'Dismiss',
              onClick: clearError,
            },
          ]}
        >
          {error}
        </Banner>
      )}

      {savedCount === 0 ? (
        <EmptyState>
          <Body size="sm" color="secondary">
            No references saved yet.{' '}
            {onGoToStep3 && (
              <>
                <BodyLink
                  size="sm"
                  underline
                  style={{ color: 'var(--text-link)' }}
                  onClick={onGoToStep3}
                >
                  Go back to the suggestions step
                </BodyLink>{' '}
                to add references by clicking &quot;Add to paper&quot; on any
                suggested work.
              </>
            )}
            {!onGoToStep3 && (
              <>
                Add references by clicking &quot;Add to paper&quot; on any
                suggested work.
              </>
            )}
          </Body>
        </EmptyState>
      ) : (
        <>
          <ReferencesList>
            {savedReferences.map((reference, index) => {
              const getStableKey = () => {
                if (reference.work?.id) {
                  return `id-${reference.work.id}`;
                }
                if (reference.work?.title) {
                  const titleHash = reference.work.title
                    .split('')
                    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
                  return `title-${titleHash}-${index}`;
                }
                return `fallback-${index}`;
              };

              return (
                <WorkCard
                  key={getStableKey()}
                  workData={reference}
                  showAddToListButton={true}
                  showPaperEvaluationChatButton={false}
                  pageContext="preprint_report"
                />
              );
            })}
          </ReferencesList>

          <ExportBar>
            <ExportButton style={{ flex: 2 }}>
              <Button
                secondary
                size="sm"
                icon="file_download"
                disabled={loadingButton === 'bibtex'}
                trackClick={{
                  source: 'citeright.references_export',
                  entityId: reportId || undefined,
                  extra: {
                    format: 'bibtex',
                    reference_count: savedReferences.length,
                  },
                }}
                onClick={handleDownloadBibtex}
              >
                Download saved references for BibTeX/LaTeX
              </Button>
            </ExportButton>
            <ExportButton style={{ flex: 1 }}>
              <Button
                secondary
                size="sm"
                icon="content_copy"
                disabled={loadingButton === 'copy'}
                trackClick={{
                  source: 'citeright.references_copy',
                  entityId: reportId || undefined,
                  extra: { reference_count: savedReferences.length },
                }}
                onClick={handleCopyCitations}
              >
                Copy saved references
              </Button>
            </ExportButton>
          </ExportBar>
        </>
      )}
    </Container>
  );
};

const Container = styled.div``;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  gap: 8px;

  @media (max-width: 567px) {
    flex-direction: column;
    align-items: flex-start;

    button {
      width: 100%;
    }
  }
`;

const ReferencesList = styled.div`
  max-height: 60vh;
  overflow-y: auto;
`;

const ExportBar = styled.div`
  display: flex;
  gap: 14px;
  padding: 12px 0;

  @media (max-width: 567px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ExportButton = styled.div`
  flex: 1;

  button {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

export default PreprintExportStep;
